<template>
  <div>
    <section>
      <div class="carousel-cell">
        <img
          :alt="slideArr[0].alt_text"
          height="460"
          width="850"
          fetchpriority="high"
          :srcset="`
            ${slideArr[0].image}?width=640 640w,
            ${slideArr[0].image}?width=1352 1352w,
            ${slideArr[0].image}?width=850 850w`"
          sizes="
            (min-width: 1200px) 850px,
            (min-width: 768px) 1352px,
            100vw"
          :src="`${slideArr[0].image}?width=850`"
        >
      </div>
      <div class="mag-glass-container">
        <div
          class="mag-glass p-3"
          @click="() => openModal()"
          @keypress="() => openModal()"
        >
          <font-awesome-icon icon="search-plus" />
        </div>
      </div>
    </section>
    <transition name="fade">
      <ProductsCarouselModal
        v-if="showModal"
        :slides="slideArr"
        :thumbnails="thumbnails"
        :initial-slide="currentSlide"
        class="products-modal-container"
        @close-modal="closeModal"
      />
    </transition>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ProductsCarouselModal from './ProductsCarouselModal.vue';

export default {
  components: {
    FontAwesomeIcon,
    ProductsCarouselModal,
  },
  props: {
    images: {
      type: Array,
      default: () => [],
      required: true,
    },
    videos: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      slideArr: [],
      showModal: false,
      currentSlide: 0,
    };
  },
  watch: {
    // jl: open carousel modal when user clicks on story image (overview section)
    '$store.state.carouselModal.targetImageSlug': function (targetImageSlug) {
      if (targetImageSlug && !this.showModal) {
        this.openModalToSlug(targetImageSlug);
      }
    },
  },
  async created() {
    const gallery = [...this.images];

    // an: insert at index 1 all Videos,
    gallery.splice(1, 0, ...this.videos);

    this.slideArr = gallery.map((item) => {
      const data = { ...item };

      if (data.embed_code) {
        data.embed_loaded = false;
      }

      return data;
    });

    this.thumbnails = this.slideArr.map((item) => ({
      image: this.getThumbnailImage(item),
      embedVideo: item.embed_code,
    }));
  },
  methods: {
    getThumbnailImage(item) {
      if (item.image) {
        return `${item.image}?width=150`;
      }

      if (item.thumbnail) {
        return `${item.thumbnail}?width=150`;
      }

      return item.embed_thumbnails?.sm || '';
    },
    openModalToSlug(targetImageSlug) {
      // jl: when user clicks html_body/story image in overview, let's find and set the currentSlide
      //     to the target image *before* we open the modal
      let slideIndex = this.slideArr.findIndex((slide) => (
        slide.image && slide.slug === targetImageSlug
      ));

      // jw: if we couldn't identify the image from the slug just default to the first one.
      if (slideIndex === -1) {
        slideIndex = 0;
      }

      this.openModal(slideIndex);
    },
    openModal(slideIndex = 0) {
      this.currentSlide = slideIndex;
      this.showModal = true;

      this.updateScrollToTop();
      this.updateModalScrollClass();
    },
    closeModal() {
      this.showModal = false;

      // jl: reset state only if modal was opened via story image click
      if (this.$store.state.carouselModal.targetImageSlug) {
        this.$store.dispatch('carouselModal/closeModal');
      }

      this.updateScrollToTop();
      this.updateModalScrollClass();
    },
    updateScrollToTop() {
      this.$store.dispatch(
        'scrollToTop/updateVisibility',
        { hideScrollToTop: this.showModal },
      );
    },
    updateModalScrollClass() {
      // Hide page scrollbars when modal is open
      if (document && this.showModal) {
        document.body.classList.add('carousel-hide-scroll');
      } else if (document) {
        document.body.classList.remove('carousel-hide-scroll');
      }
    },
  },
};
</script>

<style>
  /* Hide page scrollbars when modal is open
     an: we need it out of the scope of the component */
  .carousel-hide-scroll {
    overflow: hidden;
  }
</style>

<style lang="scss" scoped>
  @import '~/styles/dorado/themes/dorado';
  @import '~/styles/utils/breakpoints';

  .mag-glass-container {
    position: relative;
    float: right;
    z-index: 1;
    cursor: pointer;

    .mag-glass {
      position: absolute;
      right: 100%;
      bottom: 100%;
      transition: transform 0.2s;

      &:hover {
        transform: scale(1.2);
      }

      > svg {
        font-size: 1.5rem;
      }
    }
  }

  .carousel-cell {
    width: 100%;
    height: auto;
    border-radius: 5px;
    position: relative;

    img {
      object-fit: cover;
      width: 100%;
      border: 1px solid $gray-300;
      border-radius: 5px;
      height: auto;
      max-height: 230px;
    }
  }

  @include media-breakpoint-up(md) {
    .carousel-cell img {
      max-height: 450px;
    }
  }

  @include media-breakpoint-up(lg) {
    .carousel-cell img {
      max-height: 490px;
    }
  }

  /* Modal Transition */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 400ms;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

</style>
